import React, { useMemo, useState, useEffect, useRef } from 'react';
import { X, MessageCircle, User, ArrowLeft, Send, Mail, CheckCircle, Archive, Paperclip } from 'lucide-react';
import { collection, doc } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import MessageBubble, { DateDivider } from './MessageBubble';
import { useAdminSupportMessages } from './useAdminSupportMessages';
import { useMessageDraft } from './useMessageDraft';
import { useAutoGrowTextarea } from './useAutoGrowTextarea';
import { DraftStatus } from './DraftStatus';
import { useImageUpload } from './useImageUpload';
import { db } from '../../firebase/firebaseConfig';

function formatMessageTime(date) {
  if (!date) return '';
  const now = new Date();
  const diff = now - date;
  const diffMinutes = Math.floor(diff / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'now';
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ---------- Embedded Chat View ----------

function InboxChatView({ accountUUID, userEmail, userName, onBack }) {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const {
    messages,
    loading,
    error,
    sending,
    conversationData,
    sendMessage,
    markAsRead,
    updateStatus
  } = useAdminSupportMessages(accountUUID);

  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    cancelUploads,
    hasImages
  } = useImageUpload();

  // Composer text is a Firestore-backed draft, not local state — it survives
  // switching conversation, closing the inbox, and a reload.
  const {
    draft: newMessage,
    setDraft: setNewMessage,
    clearDraft,
    restoreDraft,
    saveState,
    lastSavedAt,
    draftLoaded,
    loadError
  } = useMessageDraft(accountUUID);

  // Height tracks the text itself, so a draft loaded from Firestore or restored
  // after a failed send is shown at full height, not collapsed to one row.
  useAutoGrowTextarea(inputRef, newMessage);

  const busy = sending || uploading;
  // Locked only while the draft read is still in flight, so a first keystroke
  // cannot be clobbered by a late-arriving load. A read that failed for good
  // must NOT lock the composer: drafting is off, but replying still works.
  const composerLocked = !draftLoaded && !loadError;

  useEffect(() => {
    return () => cancelUploads();
  }, [cancelUploads]);

  useEffect(() => {
    if (messages.length > 0) markAsRead();
  }, [messages, markAsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if ((!newMessage.trim() && !hasImages) || busy || composerLocked) return;
    const messageToSend = newMessage;
    setNewMessage('');

    let sent = false;
    if (hasImages) {
      setUploading(true);
      try {
        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const newMessageRef = doc(messagesRef);
        const messageId = newMessageRef.id;
        const uploadedImages = await uploadImages(accountUUID, messageId);
        clearImages();
        setUploading(false);
        sent = await sendMessage(messageToSend, uploadedImages, messageId);
      } catch (err) {
        console.error('Error uploading images:', err);
        setUploading(false);
        restoreDraft(messageToSend);
        return;
      }
    } else {
      sent = await sendMessage(messageToSend);
    }

    // Only drop the stored draft once the message is really in Firestore;
    // otherwise put the text back so a failed send never destroys the work.
    if (sent) {
      await clearDraft();
    } else {
      restoreDraft(messageToSend);
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      e.preventDefault();
      addImages(files);
    }
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current++; if (e.dataTransfer.types.includes('Files')) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounterRef.current--; if (dragCounterRef.current === 0) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false); dragCounterRef.current = 0;
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) addImages(files);
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;
    messages.forEach((message) => {
      const messageDate = message.createdAt instanceof Date
        ? message.createdAt.toDateString()
        : new Date(message.createdAt).toDateString();
      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ type: 'date', date: message.createdAt });
      }
      groups.push({ type: 'message', message });
    });
    return groups;
  };

  const status = conversationData?.status || 'open';

  return (
    <div
      className="flex flex-col h-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Chat header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 transition-colors">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900 truncate">{userName || 'User'}</h2>
              {status === 'closed'
                ? <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-xs">Closed</Badge>
                : <Badge variant="default" className="bg-green-100 text-green-700 text-xs">Open</Badge>
              }
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1 truncate">
              <Mail className="h-3 w-3" />
              {userEmail}
            </p>
          </div>
          {status !== 'closed' ? (
            <Button variant="outline" size="sm" onClick={() => updateStatus('closed')} className="flex items-center gap-1 text-xs">
              <Archive className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Close</span>
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => updateStatus('open')} className="flex items-center gap-1 text-xs">
              <CheckCircle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Reopen</span>
            </Button>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-br from-slate-50 to-slate-100 relative">
        {isDragging && (
          <div className="absolute inset-0 bg-blue-600/20 border-2 border-dashed border-blue-400 z-10 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-xl px-6 py-4 shadow-lg">
              <p className="text-slate-700 font-medium">Drop images here</p>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-400">Loading messages...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium mb-1">No messages yet</p>
              <p className="text-sm text-gray-400">Start the conversation below.</p>
            </div>
          </div>
        ) : (
          <div>
            {groupMessagesByDate(messages).map((item, index) => (
              item.type === 'date' ? (
                <DateDivider key={`date-${index}`} date={item.date} />
              ) : (
                <MessageBubble key={item.message.id} message={item.message} isOwn={item.message.senderType === 'admin'} />
              )
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Image preview strip */}
      {pendingImages.length > 0 && (
        <div className="flex-shrink-0 bg-white border-t border-gray-200 px-2 pb-2">
          <div className="flex gap-2 overflow-x-auto pt-3 px-1">
            {pendingImages.map((img) => (
              <div key={img.id} className="relative flex-shrink-0 w-16 h-16">
                <img src={img.previewUrl} alt="Pending" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                {img.status === 'uploading' && (
                  <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{img.progress}%</span>
                  </div>
                )}
                {img.status === 'error' && (
                  <div className="absolute inset-0 bg-red-500/40 rounded-lg flex items-center justify-center">
                    <X className="h-4 w-4 text-white" />
                  </div>
                )}
                {img.status !== 'uploading' && (
                  <button className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors" onClick={() => removeImage(img.id)}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 p-3">
        <div className="flex gap-2 items-center">
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" multiple
            onChange={(e) => { if (e.target.files.length > 0) addImages(Array.from(e.target.files)); e.target.value = ''; }}
          />
          <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} disabled={busy} className="flex-shrink-0 h-11 w-11 text-gray-400 hover:text-gray-600">
            <Paperclip className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={composerLocked ? 'Loading draft...' : 'Type your reply...'}
              // `busy`, not just `sending`: text typed during an image upload
              // would block restoreDraft from putting the message back if the
              // upload then failed.
              disabled={busy || composerLocked}
              rows={1}
              className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50"
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
          </div>
          <Button onClick={handleSend} disabled={(!newMessage.trim() && !hasImages) || busy || composerLocked} className="flex-shrink-0 h-11 bg-blue-600 hover:bg-blue-700">
            {busy ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <Send className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Send</span>
              </>
            )}
          </Button>
        </div>
        <DraftStatus saveState={saveState} lastSavedAt={lastSavedAt} loadError={loadError} mutedClassName="text-gray-400" />
      </div>
    </div>
  );
}

// ---------- Main Full-Screen Inbox ----------

function MessageInbox({ conversations, users, drafts = {}, onClose }) {
  const [activeChat, setActiveChat] = useState(null); // { id, name, email }

  // Close on Escape (go back to list first, then close)
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (activeChat) setActiveChat(null);
        else onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, activeChat]);

  // Build user lookup
  const userLookup = useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.id] = { name: u.name, email: u.email }; });
    return map;
  }, [users]);

  // Enrich and sort conversations
  const enrichedConversations = useMemo(() => {
    return conversations
      .map(conv => ({
        ...conv,
        userName: userLookup[conv.id]?.name || 'Unknown User',
        userEmail: userLookup[conv.id]?.email || conv.id,
      }))
      .sort((a, b) => {
        const aTime = a.lastMessageAt?.getTime() || 0;
        const bTime = b.lastMessageAt?.getTime() || 0;
        return bTime - aTime;
      });
  }, [conversations, userLookup]);

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadByAdmin || 0), 0);

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {activeChat ? (
        // Chat view
        <InboxChatView
          accountUUID={activeChat.id}
          userEmail={activeChat.email}
          userName={activeChat.name}
          onBack={() => setActiveChat(null)}
        />
      ) : (
        // Conversation list view
        <>
          {/* Header */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-gray-700" />
              <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
              {totalUnread > 0 && (
                <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">
                  {totalUnread > 99 ? '99+' : totalUnread} unread
                </Badge>
              )}
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {enrichedConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <MessageCircle className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-lg font-medium text-gray-500">No conversations yet</p>
                <p className="text-sm text-gray-400 mt-1">Messages from users will appear here</p>
              </div>
            ) : (
              enrichedConversations.map(conv => {
                const unread = conv.unreadByAdmin || 0;
                const isOpen = conv.status === 'open';
                // An unsent draft this admin prepared for this account. Shown
                // instead of the last-message preview, since it is the thing
                // waiting on you.
                const draft = drafts[conv.id];

                return (
                  <button
                    key={conv.id}
                    onClick={() => setActiveChat({ id: conv.id, name: conv.userName, email: conv.userEmail })}
                    className={`w-full text-left px-4 py-4 hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 flex items-center gap-4 ${
                      unread > 0 ? 'bg-blue-50/40' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold ${
                      unread > 0
                        ? 'bg-blue-100 text-blue-700'
                        : isOpen
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-400'
                    }`}>
                      {conv.userName !== 'Unknown User'
                        ? conv.userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        : <User className="w-5 h-5" />
                      }
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm truncate ${unread > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {conv.userName}
                        </span>
                        <span className={`text-xs flex-shrink-0 ${unread > 0 ? 'text-blue-500 font-medium' : 'text-gray-400'}`}>
                          {conv.lastMessageAt && formatMessageTime(conv.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        {draft ? (
                          <p className="text-sm truncate flex items-center gap-1.5 min-w-0">
                            <span className="flex-shrink-0 bg-blue-100 text-blue-700 text-xs font-medium rounded-full px-1.5 py-0.5">
                              Draft
                            </span>
                            <span className="text-blue-700 truncate">{draft.content}</span>
                          </p>
                        ) : (
                          <p className={`text-sm truncate ${unread > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                            {conv.lastMessagePreview || 'No messages yet'}
                          </p>
                        )}
                        {unread > 0 && (
                          <span className="flex-shrink-0 bg-blue-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
                            {unread > 9 ? '9+' : unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MessageInbox;
